import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Loader } from '@googlemaps/js-api-loader'

const loader = new Loader({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    version: 'weekly',
})

const LocationPicker = ({ value, onChange, disabled, required }) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [inputValue, setInputValue] = useState(value || '')
    const [suggestions, setSuggestions] = useState([])
    const [open, setOpen] = useState(false)
    const sessionTokenRef = useRef(null)
    const placesRef = useRef(null)

    useEffect(() => {
        loader.importLibrary('places')
            .then(places => {
                placesRef.current = places
                setIsLoaded(true)
            })
            .catch(console.error)
    }, [])

    useEffect(() => {
        setInputValue(value || '')
    }, [value])

    const fetchSuggestions = useCallback(async (query) => {
        if (!query || query.length < 2 || !placesRef.current) {
            setSuggestions([])
            return
        }
        const { AutocompleteSuggestion, AutocompleteSessionToken } = placesRef.current
        if (!sessionTokenRef.current) {
            sessionTokenRef.current = new AutocompleteSessionToken()
        }
        try {
            const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: query,
                sessionToken: sessionTokenRef.current,
            })
            setSuggestions(suggestions || [])
            setOpen(true)
        } catch (err) {
            console.error(err)
            setSuggestions([])
        }
    }, [])

    const handleChange = (e) => {
        const val = e.target.value
        setInputValue(val)
        if (val) {
            fetchSuggestions(val)
        } else {
            setSuggestions([])
            setOpen(false)
            onChange({ name: '', placeId: '' })
        }
    }

    const selectSuggestion = async (suggestion) => {
        try {
            const placeId = suggestion.placePrediction.placeId
            const place = suggestion.placePrediction.toPlace()
            await place.fetchFields({ fields: ['displayName', 'formattedAddress'] })
            const name = place.formattedAddress || place.displayName?.text || ''
            setInputValue(name)
            setSuggestions([])
            setOpen(false)
            sessionTokenRef.current = null
            // Hand back the address and the place ID, not a finished URL. The map
            // link is built at render time by utils/locationLink.js.
            onChange({ name, placeId })
        } catch (err) {
            console.error(err)
        }
    }

    return (
        <div style={{ position: 'relative' }}>
            <input
                className='form-control'
                type='text'
                value={inputValue}
                onChange={handleChange}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                disabled={disabled || !isLoaded}
                required={required}
                placeholder={isLoaded ? 'Search for a location…' : 'Loading maps…'}
                autoComplete='off'
            />
            {open && suggestions.length > 0 && (
                <ul style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#fff',
                    border: '1px solid #ced4da',
                    borderTop: 'none',
                    borderRadius: '0 0 0.375rem 0.375rem',
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    zIndex: 1050,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '240px',
                    overflowY: 'auto',
                }}>
                    {suggestions.map((s, i) => (
                        <li
                            key={i}
                            onMouseDown={() => selectSuggestion(s)}
                            style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: i < suggestions.length - 1 ? '1px solid #f1f1f1' : 'none' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            {s.placePrediction.text.text}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default LocationPicker
